import { ReactNode } from "react";
import PageNotFound from "../PageNotFound";
import Loading from "../Widgets/Loading";
import { useAudience } from "./useAudience";
import { useCerberus } from "../../hooks/useCerberus";
import NoRolesAssigned from "./NoRolesAssigned";

/**
 * Renders a different element per audience for a path that means different things to different
 * users — /dashboard, /messages, /account, /patients and friends. An audience with no entry gets
 * PageNotFound, which is what it saw back when its route tree simply had no such path.
 *
 * Guards (RequireAuth / RequireRole) belong INSIDE each branch, not around this component: an
 * audience without a branch must fall through to PageNotFound rather than hit a guard's redirect.
 *
 * The exception is a signed-in account with NO role: it has no audience at all, and used to get a
 * 404 on /dashboard — the first page after sign-in — with no idea why. It gets the explanation.
 */
const ByRole = ({
  clinical,
  patient,
  external,
  caregiver,
}: {
  clinical?: ReactNode;
  patient?: ReactNode;
  external?: ReactNode;
  caregiver?: ReactNode;
}) => {
  const { audience, loading } = useAudience();
  const { userId, roles } = useCerberus();

  if (loading) return <Loading />;
  if (userId && !roles?.length) return <NoRolesAssigned />;

  const element = audience
    ? { clinical, patient, external, caregiver }[audience]
    : undefined;

  return <>{element ?? <PageNotFound />}</>;
};

export default ByRole;
